import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Offcanvas } from "react-bootstrap";
import Form from "../Helpers/Form/Form";
import { popupcontext } from "../../context/Popupscontext";
import FormHeader from "../Helpers/Form/FormParts/FormHeader";
import { useSelector } from "react-redux";
import NotificationPopup from "./NotificationPopup";
import trans from "../../utils";
import { SpeedUnit } from "../../JsHelpers/PlatformUnits";
import {
  useGetNewNotficationsQuery,
  useGetNotificationsCountQuery,
  useGetUnreadNotficationsQuery,
  useMarkAllNotificationsMutation,
  useMarkNotificationMutation,
} from "../../Redux/service/Notifications/NewNotifications";
import { formatDateString } from "../../JsHelpers/DateFormat";
import { toast } from "react-toastify";
import Button from "../Helpers/Button/Button";
import { 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Bell,
  ClipboardCheck,
  FileWarning,
  Send,
  File,
  XCircle
} from "lucide-react";

export default function SideEvents() {
  const { displayNotification, setDisplayAlert, setDisplayNotification } =
    useContext(popupcontext);

  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState([]);
  const [active, setActive] = useState("all");

  const inf = useSelector((state) => state.users.user);
  const lang = localStorage.getItem("lang") || "en";

  const scrollRef = useRef(null);
  const isFetchingRef = useRef(false);

  const [vehicledata, setVehicleData] = useState({
    id: null,
    name: null,
    geofencename: null,
    lat: null,
    lon: null,
    time: null,
    speed: null,
    speedLimit: null,
  });
  const [pid, setPId] = useState();

  const {
    data: NotificationData,
    isLoading: isLoadingAll,
    isFetching: isFetchingAll,
  } = useGetNewNotficationsQuery(
    { page, limit: 10 },
    {
      skip: !displayNotification || active !== "all",
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  const {
    data: UnreadNotificationData,
    isLoading: isLoadingUnread,
    isFetching: isFetchingUnread,
  } = useGetUnreadNotficationsQuery(
    { page, limit: 10 },
    {
      skip: !displayNotification || active !== "unread",
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { data: unReadcount } = useGetNotificationsCountQuery(undefined, {
    skip: !displayNotification,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [markNotification] = useMarkNotificationMutation();
  const [markAllNotification] = useMarkAllNotificationsMutation();

  // Helper to normalize API response
  const normalizeResponse = useCallback((response) => {
    if (!response) return { data: [], currentPage: 1, totalPages: 1 };

    // Handle FSM response: { success, notifications, pagination }
    if (response.notifications && response.pagination) {
      return {
        data: response.notifications.map(n => ({
          ...n,
          source: 'fsm',
          createdAt: n.created_at,
          updatedAt: n.updated_at,
        })),
        currentPage: response.pagination.current_page,
        totalPages: response.pagination.total_pages,
        totalItems: response.pagination.total_items,
      };
    }

    // Handle legacy response: { data, page, totalPages }
    if (response.data) {
      return {
        data: response.data.map(n => ({
          ...n,
          source: n.source || 'legacy',
        })),
        currentPage: response.page,
        totalPages: response.totalPages,
        totalItems: response.totalCount,
      };
    }

    return { data: [], currentPage: 1, totalPages: 1 };
  }, []);

  const currentData = useMemo(() => {
    const rawData = active === "all" ? NotificationData : UnreadNotificationData;
    return normalizeResponse(rawData);
  }, [active, NotificationData, UnreadNotificationData, normalizeResponse]);

  const isLoading = active === "all" ? isLoadingAll : isLoadingUnread;
  const isFetching = active === "all" ? isFetchingAll : isFetchingUnread;

  const hasMore = useMemo(() => {
    return currentData.currentPage < currentData.totalPages;
  }, [currentData]);

  // Reset when closing panel
  useEffect(() => {
    if (!displayNotification) {
      setActive("all");
      setPage(1);
      setAllData([]);
      isFetchingRef.current = false;
    }
  }, [displayNotification]);

  // Update allData when data arrives
  useEffect(() => {
    if (!currentData?.data?.length || !displayNotification) return;

    if (page === 1) {
      setAllData(currentData.data);
    } else {
      setAllData((prev) => {
        const ids = new Set(prev.map((i) => `${i.source || 'legacy'}-${i.id}`));
        const newOnes = currentData.data.filter(
          (i) => !ids.has(`${i.source || 'legacy'}-${i.id}`)
        );
        if (newOnes.length === 0) return prev;
        return [...prev, ...newOnes];
      });
    }

    isFetchingRef.current = false;
  }, [currentData, page, displayNotification]);

  // Tab switching
  const handleOptionClick = useCallback(
    (option) => {
      if (active === option) return;
      setActive(option);
      setPage(1);
      setAllData([]);
      isFetchingRef.current = false;
    },
    [active]
  );

  // Scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (isFetchingRef.current || isFetching || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (atBottom) {
      isFetchingRef.current = true;
      setPage((p) => p + 1);
    }
  }, [isFetching, hasMore]);

  // Get FSM icon based on notification type
  const getFsmIcon = useCallback((notification) => {
    const type = (notification.type || "").toLowerCase();
    const title = (notification.title || "").toLowerCase();

    if (type.includes("ir") || title.includes("inspection request") || title.includes("ir submitted")) {
      if (title.includes("rejected")) {
        return <XCircle size={24} color="#dc3545" />;
      }
      if (title.includes("approved")) {
        return <CheckCircle size={24} color="#198754" />;
      }
      return <ClipboardCheck size={24} color="#0d6efd" />;
    }
    
    if (type.includes("snag") || title.includes("snag")) {
      return <AlertTriangle size={24} color="#dc3545" />;
    }
    
    if (type.includes("submittal") || title.includes("submittal")) {
      return <Send size={24} color="#198754" />;
    }
    
    if (type.includes("approved") || title.includes("approved")) {
      return <CheckCircle size={24} color="#198754" />;
    }
    if (type.includes("rejected") || title.includes("rejected")) {
      return <XCircle size={24} color="#dc3545" />;
    }
    
    if (type.includes("document") || title.includes("document")) {
      return <File size={24} color="#6c757d" />;
    }
    
    return <Bell size={24} color="#0d6efd" />;
  }, []);

  // Get legacy alert icon
  const getAlertIcon = useCallback((notification) => {
    const iconMap = {
      deviceOverspeed: "Overspeed",
      geofenceExit: "GeoExit",
      geofenceEnter: "GeoEnter",
      deviceUnknown: "Deviceunknown",
      deviceOnline: "Deviceonline",
      deviceOffline: "Deviceoffline",
      deviceMoving: "VehicleMoving",
      deviceStopped: "Devicestopped",
      ignitionOn: "Ignitionon",
      ignitionOff: "Ignitionoff",
      deviceFuelDrop: "Fueldrop",
      deviceFuelIncrease: "Fuelincrease",
      idle: "Idle",
      maintenance: "Maintenance",
    };

    return iconMap[notification?.type] || "Notification";
  }, []);

  // Get time difference
  const getTimeDifference = useCallback((eventTime) => {
    if (!eventTime) return "";

    const eventDate = new Date(eventTime);
    const now = new Date();
    const diffMs = now - eventDate;

    if (diffMs < 0) return "";

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (lang === "ar") {
      if (minutes < 1) return "الآن";
      if (minutes < 60) return `منذ ${minutes}د`;
      if (hours < 24) return `منذ ${hours}س`;
      return `منذ ${days}ي`;
    }

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }, [lang]);
const formatFullDateTime = useCallback((eventTime) => {
  if (!eventTime) return "";
  
  const d = new Date(eventTime);
  if (isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day}/${month}/${year} ${pad(hours)}:${minutes}:${seconds} ${ampm}`;
}, []);
  // Get notification time
  const getNotificationTime = useCallback((notification) => {
    return notification?.createdAt || notification?.created_at || notification?.time;
  }, []);

  // Check if unread
  const isUnread = useCallback((notification) => {
    if (notification?.source === "fsm") {
      return notification.status === "unread";
    }
    return notification?.isRead === 0;
  }, []);

  // Mark single notification
  const handleNotificationClick = useCallback(
    async (i) => {
      const isFsm = i?.source === "fsm";
      const unread = isFsm ? i.status === "unread" : i.isRead === 0;

      if (!isFsm) {
        setVehicleData({
          id: i?.id,
          name: i?.device?.name,
          geofencename: i?.geofence?.name,
          lat: Number(i?.position?.latitude),
          lon: Number(i?.position?.longitude),
          time: i?.time,
          speed: i?.attributes?.speed,
          speedLimit: i?.attributes?.speedLimit,
        });

        if (["deviceOverspeed", "geofenceExit", "geofenceEnter"].includes(i.type)) {
          setDisplayNotification(false);
          setDisplayAlert(true);
        }
      }

      if (unread) {
        try {
          await markNotification({
            notificationId: i.id,
            source: isFsm ? "fsm" : "legacy",
          }).unwrap();

          setAllData((prev) =>
            prev.map((x) =>
              x.id === i.id && (x.source || 'legacy') === (i.source || 'legacy')
                ? { ...x, isRead: 1, status: "read" }
                : x
            )
          );
        } catch (err) {
          console.error("Failed to mark notification:", err);
        }
      }
    },
    [markNotification, setDisplayNotification, setDisplayAlert]
  );

  // Mark all as read
  const readAllNotification = useCallback(async () => {
    try {
      await markAllNotification().unwrap();
      setAllData((prev) =>
        prev.map((x) => ({ ...x, isRead: 1, status: "read" }))
      );
      toast.success(trans("All notifications marked as read"), {
        autoClose: 800,
        rtl: lang === "ar",
      });
    } catch (err) {
      console.error("Failed to mark all:", err);
      toast.error(trans("Error while reading notifications"), {
        autoClose: 800,
        rtl: lang === "ar",
      });
    }
  }, [markAllNotification, lang]);

  // Handle unread count - supports both { count } and { unread_count }
  const notificationCount = useMemo(() => {
    const count = unReadcount?.count ?? unReadcount?.unread_count ?? 0;
    return count >= 99 ? "99+" : count;
  }, [unReadcount]);

  return (
    <div className="side-events overflow-x-hidden">
      <Form show={displayNotification} other={true}>
        <Offcanvas.Header>
          <FormHeader title={trans("notifications")} other={true} />
        </Offcanvas.Header>
        <div className="H-line"></div>

        <div className="side-notifications-options">
          <div className="row">
            <div className="col-lg-6" onClick={() => handleOptionClick("all")}>
              <div
                className={`side-notifications-option ${
                  active === "all" ? "side-notifications-option-active" : ""
                }`}
              >
                {trans("all")}
              </div>
            </div>

            <div className="col-lg-6" onClick={() => handleOptionClick("unread")}>
              <div
                className={`side-notifications-option ${
                  active === "unread" ? "side-notifications-option-active" : ""
                }`}
              >
                <div className="d-flex justify-content-center align-items-center">
                  <div>{trans("unread")}</div>
                  <div className="side-notifications-option-count">
                    {notificationCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Offcanvas.Body
          onScroll={handleScroll}
          ref={scrollRef}
          style={{ overflowY: "auto", maxHeight: "100vh" }}
        >
          {isLoading && page === 1 ? (
            <div className="text-center py-4">{trans("Loading...")}</div>
          ) : allData.length === 0 ? (
            <div className="text-center py-4">{trans("No notifications")}</div>
          ) : (
            <>
              {allData.map((i) => (
                <div
                  className={`side-notification ${isUnread(i) ? "side-notification-active" : ""}`}
                  onClick={() => handleNotificationClick(i)}
                  key={`${i.source || 'legacy'}-${i.id}`}
                >
                  <div className="d-flex">
                    <div className="me-2 notification-icon">
                      {i?.source === "fsm" ? (
                        getFsmIcon(i)
                      ) : (
                        <img
                          src={`${process.env.PUBLIC_URL}/assets/New/Alerts/${getAlertIcon(i)}.svg`}
                          alt="Notify"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${process.env.PUBLIC_URL}/assets/New/Alerts/Notification.svg`;
                          }}
                        />
                      )}
                    </div>

                    <div className="side-notification-content">
                      <div className="side-notification-content-title">
                        <div className="side-notification-type">
                          {i.title || trans("Notification")}
                        </div>
                        <div style={{ color: "#7A869A", fontSize: "14px" }}>
                          {getTimeDifference(getNotificationTime(i))}
                        </div>
                      </div>
<div className="side-notification-message">
  {i.message} at  <span style={{ color: "#7A869A", fontSize: "12px", marginTop: "4px" }}>
  {formatFullDateTime(getNotificationTime(i))}
</span>
</div>

                    </div>
                  </div>
                </div>
              ))}

              {isFetching && page > 1 && (
                <div className="text-center py-2">{trans("Loading more...")}</div>
              )}

              {!hasMore && allData.length > 0 && (
                <div className="text-center py-2 text-muted">
                  {trans("No more notifications")}
                </div>
              )}
            </>
          )}
        </Offcanvas.Body>

        <div className="offcanvas-footer d-flex justify-content-end w-100 m-0">
          <Button
            type="primary"
            htmlType="button"
            text={trans("Mark all as read")}
            onClick={readAllNotification}
          />
        </div>
      </Form>

      <NotificationPopup positionId={pid} vehicleData={vehicledata} />
    </div>
  );
}